import DataTable from 'react-data-table-component';
import theme from '../../styles/theme.tsx';
import Card from '../card/Card.tsx';
import P from '../texts/p/P.tsx';
import Button from '../button/Button.tsx';

type TableCustomStyles = {
  table?: { style?: Record<string, unknown> };
  responsiveWrapper?: { style?: Record<string, unknown> };
  headCells?: { style?: Record<string, unknown> };
  cells?: { style?: Record<string, unknown> };
  [key: string]: unknown;
};

type TableProps = {
  columns: any[];
  data: any[];
  tableData: any;
  handleChangePage?: (page: number) => void;
  onRowClicked?: (row: any) => void;
  variant?: 'normal' | 'weak';
  onSort?: (field: string, direction?: 'asc' | 'desc') => void;
  hideNativeSortIcon?: boolean;
  conditionalRowStyles?: any[];
  disablePagination?: boolean;
  /** Sobrescribe estilos del variant (merge por clave: responsiveWrapper, cells, etc.). */
  customStyles?: TableCustomStyles;
  expandableRows?: boolean;
  expandableRowsComponent?: (row: any) => React.ReactNode;
  expandableRowDisabled?: (row: any) => boolean;
  expandableRowExpanded?: (row: any) => boolean;
  onRowExpandToggled?: (expanded: boolean, row: any) => void;
  keyField?: string;
  noDataComponent?: React.ReactNode;
  persistTableHead?: boolean;
};

const mergeStyles = (
  base: Record<string, { style?: Record<string, unknown> }>,
  overrides?: TableCustomStyles
): Record<string, { style?: Record<string, unknown> }> => {
  if (!overrides) return base;
  const result = { ...base };
  for (const key of Object.keys(overrides)) {
    if (overrides[key] && typeof overrides[key] === 'object' && key !== 'style') {
      const baseEntry = result[key] as { style?: Record<string, unknown> } | undefined;
      const overrideEntry = overrides[key] as { style?: Record<string, unknown> } | undefined;
      if (overrideEntry?.style) {
        result[key] = {
          ...(baseEntry || {}),
          style: { ...(baseEntry?.style || {}), ...overrideEntry.style },
        };
      } else if (overrideEntry) {
        result[key] = { ...(baseEntry || {}), ...overrideEntry };
      }
    }
  }
  return result;
};

const Table: React.FC<TableProps> = ({
  columns,
  data,
  tableData,
  handleChangePage,
  onRowClicked,
  variant = 'normal',
  onSort,
  hideNativeSortIcon = false,
  conditionalRowStyles,
  disablePagination = false,
  customStyles,
  expandableRows,
  expandableRowsComponent,
  expandableRowDisabled,
  expandableRowExpanded,
  onRowExpandToggled,
  keyField = 'id',
  noDataComponent,
  persistTableHead = false,
}) => {
  const regularTableStyles = {
    table: {
      style: {
        width: '100%',
        minWidth: 'auto',
      },
    },
    responsiveWrapper: {
      style: {
        borderRadius: '0px',
        borderTop: 'solid 1px #DFE6EB',
        overflowX: 'auto',
        width: '100%',
      },
    },
    headCells: {
      style: {
        backgroundColor: theme.colors.neutral.bg.regular || '#F5F7FA',
        height: '56px',
        fontSize: 14,
        fontWeight: 'bold',
        paddingLeft: '12px',
        paddingRight: '12px',
        whiteSpace: 'nowrap',
      },
    },
    cells: {
      style: {
        height: '56px',
        cursor: 'pointer',
        paddingLeft: '12px',
        paddingRight: '12px',
        fontSize: 14,
        whiteSpace: 'nowrap',
        overflow: 'hidden',
        textOverflow: 'ellipsis',
      },
    },
  };

  const weakTableStyles = {
    table: {
      style: {
        width: '100%',
        minWidth: 'auto',
      },
    },
    responsiveWrapper: {
      style: {
        borderRadius: theme.borderRadius['border-radius-1'],
        overflowX: 'auto',
        borderBottom: 'solid 1px #DFE6EB',
        width: '100%',
      },
    },
    headCells: {
      style: {
        padding: theme.spacing['spacing-0'],
        color: theme.colors.neutral.text.strong,
        gap: theme.spacing['spacing-3'],
        fontSize: 16,
        fontWeight: 'bold',
        whiteSpace: 'nowrap',
        paddingLeft: '12px',
        paddingRight: '12px',
      },
    },
    headRow: {
      style: {
        gap: theme.spacing['spacing-3'],
      },
    },
    rows: {
      style: {
        minHeight: '36px',
        gap: theme.spacing['spacing-3'],
      },
    },
    cells: {
      style: {
        padding: theme.spacing['spacing-0'],
        fontSize: 14,
        whiteSpace: 'nowrap',
        overflow: 'hidden',
        textOverflow: 'ellipsis',
        paddingLeft: '12px',
        paddingRight: '12px',
      },
    },
  };

  const getVariant = (variant: string) =>
    variant === 'weak' ? weakTableStyles : regularTableStyles;

  const resolvedStyles = mergeStyles(getVariant(variant), customStyles);

  const CustomMaterialPagination = () => {
    const totalElements = tableData.totalElements;
    const pageSize = tableData.size;
    const realTotalPages = Math.ceil(totalElements / pageSize);

    const currentPage = tableData.page;
    const visiblePages = 5;

    const firstPage = 0;
    const lastPage = realTotalPages - 1;

    let startPage = Math.max(currentPage - Math.floor(visiblePages / 2), 1);
    let endPage = startPage + visiblePages - 1;

    if (endPage >= realTotalPages) {
      endPage = realTotalPages - 1;
      startPage = Math.max(endPage - visiblePages + 1, 1);
    }

    return (
      <Card>
        {realTotalPages > 1 && (
          <Card
            display='flex'
            alignItems='center'
            gap='10px'
            justifyContent='center'
            margin={{ top: '3' }}
          >
            {currentPage !== 0 && (
              <Button
                icon='chevron_left'
                iconAlign='left'
                variant='tertiary'
                onClick={() => handleChangePage(currentPage - 1)}
              >
                <P type='2' variant='neutral' weight='bold' withMargin={false}>
                  Anterior
                </P>
              </Button>
            )}

            <Button
              size='lg'
              variant={currentPage === firstPage ? 'primary' : 'tertiary'}
              onClick={() => handleChangePage(firstPage)}
            >
              <P
                type='2'
                variant='neutral'
                modifier={currentPage === firstPage ? 'inverted' : 'regular'}
                weight='bold'
                withMargin={false}
              >
                1
              </P>
            </Button>

            {startPage > 1 && (
              <Button
                size='lg'
                variant='tertiary'
                onClick={() => handleChangePage(startPage - 1)}
              >
                <P type='2' variant='neutral' weight='bold' withMargin={false}>
                  ...
                </P>
              </Button>
            )}

            {Array.from({ length: endPage - startPage + 1 }, (_, i) => {
              const pageIndex = startPage + i;
              return (
                <Button
                  key={pageIndex}
                  size='lg'
                  variant={currentPage === pageIndex ? 'primary' : 'tertiary'}
                  onClick={() => handleChangePage(pageIndex)}
                >
                  <P
                    type='2'
                    variant='neutral'
                    modifier={
                      currentPage === pageIndex ? 'inverted' : 'regular'
                    }
                    weight='bold'
                    withMargin={false}
                  >
                    {pageIndex + 1}
                  </P>
                </Button>
              );
            })}

            {endPage < lastPage && (
              <Button
                size='lg'
                variant='tertiary'
                onClick={() => handleChangePage(endPage + 1)}
              >
                <P type='2' variant='neutral' weight='bold' withMargin={false}>
                  ...
                </P>
              </Button>
            )}

            {currentPage !== lastPage && (
              <Button
                icon='chevron_right'
                iconAlign='right'
                variant='tertiary'
                onClick={() => handleChangePage(currentPage + 1)}
              >
                <P type='2' variant='neutral' weight='bold' withMargin={false}>
                  Siguiente
                </P>
              </Button>
            )}
          </Card>
        )}
      </Card>
    );
  };

  return (
    <div style={{ width: '100%' }}>
      <style>
        {`
        .rdt_TableCell {
            overflow: hidden;
            text-overflow: ellipsis;
            white-space: nowrap;
          }
        ${expandableRows ? `
        .rdt_TableRow .rdt_TableCell:first-child {
          padding-left: 12px;
        }
        ` : ''}
        `}
      </style>
      <DataTable
        columns={columns}
        data={data}
        keyField={keyField}
        customStyles={resolvedStyles}
        pagination={!disablePagination}
        paginationComponent={disablePagination ? undefined : CustomMaterialPagination}
        onRowClicked={onRowClicked}
        paginationPerPage={disablePagination ? data.length : (tableData.size || 10)}
        persistTableHead={persistTableHead}
        noDataComponent={
          noDataComponent ?? (
            <div style={{ padding: '24px' }} data-testid='table-no-data'>
              No se encontraron registros
            </div>
          )
        }
        sortServer={true}
        sortIcon={hideNativeSortIcon ? <span style={{ display: 'none' }} /> : undefined}
        onSort={(column: any, sortDirection: any) => {
          if (onSort && column.sortField) {
            onSort(column.sortField, sortDirection);
          }
        }}
        expandableRows={expandableRows}
        expandableRowsComponent={expandableRowsComponent}
        expandableRowDisabled={expandableRowDisabled}
        expandableRowExpanded={expandableRowExpanded}
        onRowExpandToggled={onRowExpandToggled}
        conditionalRowStyles={[
          ...(conditionalRowStyles || []),
          {
            when: (row: any) => row?.type === 'detail',
            style: {
              backgroundColor: '#F0F5FF',
              '&:hover': {
                backgroundColor: '#E8F0FF',
              },
            },
          },
        ]}
      />
    </div>
  );
};

export default Table;
